import { normaliseStage } from '../lib/workflow/transitions';
import type { CanonicalStage } from '../lib/workflow/transitions';

export type StandardPhaseKey =
  | 'draft'
  | 'submitted'
  | 'screening'
  | 'processing'
  | 'billings'
  | 'immigration'
  | 'preDeparture'
  | 'closed';

export const STANDARD_PHASES: Array<{ key: StandardPhaseKey; label: string; order: number }> = [
  { key: 'draft', label: '1. Draft', order: 1 },
  { key: 'submitted', label: '2. Submitted for Review', order: 2 },
  { key: 'screening', label: '3. Ready for Screening', order: 3 },
  { key: 'processing', label: '4. Ready for Processing', order: 4 },
  { key: 'billings', label: '5. Billings', order: 5 },
  { key: 'immigration', label: '6. Immigration', order: 6 },
  { key: 'preDeparture', label: '7. Pre-Departure', order: 7 },
  { key: 'closed', label: '8. Closure', order: 8 },
];

/** Canonical workflow stage → program phase key. The two vocabularies
 *  differ only for the screening / processing stages. */
const PHASE_KEY_BY_CANONICAL_STAGE: Record<CanonicalStage, StandardPhaseKey> = {
  draft: 'draft',
  submitted: 'submitted',
  readyForScreening: 'screening',
  readyForProcessing: 'processing',
  billings: 'billings',
  immigration: 'immigration',
  preDeparture: 'preDeparture',
  closed: 'closed',
};

/**
 * Map any stage value to the program-phase key used by phase action
 * definitions. Accepts the canonical workflow stages (the workflow
 * record's vocabulary), phase keys themselves, and legacy
 * application-doc spellings (normalised via `normaliseStage` first —
 * e.g. an application doc carrying `stage: 'processing'` or
 * `currentStage: 'readyForProcessing'`). Returns null when the value
 * maps to no phase.
 */
/** Canonical workflow stage for a program phase key (inverse of
 *  PHASE_KEY_BY_CANONICAL_STAGE). Phase keys are the Program
 *  Configuration vocabulary; stage writes must always use the canonical
 *  stage vocabulary — writing a raw phase key (e.g. 'screening') into a
 *  stage field corrupts the record, because normaliseStage does not map
 *  it and STAGE_ORDER does not contain it. */
const CANONICAL_STAGE_BY_PHASE_KEY: Record<StandardPhaseKey, CanonicalStage> = {
  draft: 'draft',
  submitted: 'submitted',
  screening: 'readyForScreening',
  processing: 'readyForProcessing',
  billings: 'billings',
  immigration: 'immigration',
  preDeparture: 'preDeparture',
  closed: 'closed',
};

export function canonicalStageForPhaseKey(key: StandardPhaseKey): CanonicalStage {
  return CANONICAL_STAGE_BY_PHASE_KEY[key];
}

/** Resolve any stage-ish input (canonical stage, phase key, or legacy
 *  spelling) to a canonical stage, or null when it maps to none. */
export function canonicalStageForInput(stage: unknown): CanonicalStage | null {
  const phaseKey = phaseKeyForStage(stage);
  return phaseKey ? CANONICAL_STAGE_BY_PHASE_KEY[phaseKey] : null;
}

export function phaseKeyForStage(stage: unknown): StandardPhaseKey | null {
  if (stage == null) return null;
  const raw = String(stage).trim();
  if (!raw) return null;
  if (STANDARD_PHASES.some((p) => p.key === raw)) return raw as StandardPhaseKey;
  const canonical = normaliseStage(raw);
  return PHASE_KEY_BY_CANONICAL_STAGE[canonical as CanonicalStage] ?? null;
}

/**
 * Resolve which phase's admin actions apply to an applicant. The
 * workflow record's stage is the source of truth; the
 * application-doc-derived value (what the phase-action-instances
 * endpoint derives server-side from `currentStage || stage`) is only
 * a fallback for applicants whose record carries no usable stage.
 * Falls back to 'draft' when neither maps — the panel's historical
 * default.
 */
export function resolvePhaseKey(recordStage: unknown, fallbackStage: unknown): StandardPhaseKey {
  return phaseKeyForStage(recordStage) ?? phaseKeyForStage(fallbackStage) ?? 'draft';
}

export type PhaseActionType =
  | 'send_email'
  | 'send_automation'
  | 'text_input'
  | 'file_input'
  | 'visual_text'
  | 'visual_file'
  | 'select_one_option'
  | 'yes_no'
  | 'date_entry';

export interface PhaseActionSelectOption {
  id: string;
  label: string;
  actionType?: 'send_email' | 'send_automation' | 'none';
  emailTemplateId?: string;
  emailSubject?: string;
  emailBodyHtml?: string;
  automationId?: string;
  instructions?: string;
}

/**
 * Conditional visibility ("show when") for an admin task: the task is
 * shown for an applicant only when the SOURCE task (another definition
 * in the same program + phase, earlier in the order, itself never
 * conditional — depth 1 only) has a stored answer and that answer is
 * one of `values`.
 *
 * OWNER RULE (2026-10-27): the option is carried DYNAMICALLY. `values`
 * holds the source's stored option VALUES — option ids for a
 * select_one_option source, 'yes'/'no' for a yes_no source — never
 * label text and never free text, because yes_no labels are configured
 * per task and option lists are arbitrary. Labels are display-only and
 * are always read from the source definition's CURRENT configuration.
 *
 * Visibility is derived at read time from sibling instances' stored
 * answers (see src/lib/phaseActionVisibility.ts); it is never persisted
 * on instances. A condition that references something that no longer
 * exists FAILS OPEN: the task stays visible and the editor flags the
 * condition as broken — a task must never silently hide forever.
 */
export interface PhaseActionShowWhen {
  sourceActionDefId: string;
  values: string[];
}

export interface ProgramPhaseActionDef {
  id: string; // Persistent UUID
  /** Owning workspace (audit phase 2, H-4). Absent = legacy global def. */
  workspaceId?: string;
  phaseKey: StandardPhaseKey;
  actionType: PhaseActionType;
  title: string;
  instructions?: string;
  required: boolean;
  requireNote?: boolean;
  sortOrder: number;
  /** IDs of other action definitions that must be completed before this action can be executed/completed. */
  prerequisiteActionDefIds?: string[];
  /** Optional conditional visibility — see PhaseActionShowWhen. */
  showWhen?: PhaseActionShowWhen;

  // Action Type Specific Configs
  config: {
    requireNote?: boolean;

    // 1. Send Email
    emailTemplateId?: string;
    emailSubject?: string;
    emailBodyHtml?: string;
    // Document templates at send time (Slice 5): additional document
    // template ids generated from the applicant and attached to the
    // completion email, on top of any the bound email template carries
    // (effective selection = template-carried ∪ this list).
    documentTemplateIds?: string[];

    // 2. Send Automation
    automationId?: string;
    automationName?: string;

    // 3. Text Input
    textPlaceholder?: string;

    // 4. File Input
    acceptedFormats?: string[];
    maxSizeMb?: number;

    // 5. Visual Text Display
    displayText?: string;

    // 6. Visual File — View Only
    referenceFileUrl?: string;
    referenceFileName?: string;

    // 7. Select One Option
    options?: PhaseActionSelectOption[];

    // 8. Yes/No
    yesLabel?: string;
    noLabel?: string;

    // 9. Date Entry
    requireDate?: boolean;
    minDate?: string;
    maxDate?: string;

    [key: string]: any;
  };
}

export interface ProgramPhaseActionInstance {
  id: string; // Instance ID
  /** Owning workspace (audit phase 2, H-4), stamped from the application. */
  workspaceId?: string;
  actionDefId: string;
  programId: string;
  phaseKey: StandardPhaseKey;
  applicationId: string;
  actionType: PhaseActionType;
  title: string;
  instructions?: string;
  required: boolean;
  requireNote?: boolean;
  status: 'pending' | 'completed' | 'failed' | 'reopened';

  // Output Payload
  savedText?: string;
  savedFileUrl?: string;
  savedFileName?: string;
  emailMessageId?: string;
  /** ES-1: name of the provider that accepted the bound send (e.g. 'zeptomail'). */
  emailProvider?: string;
  automationRunId?: string;

  // New Action Type Outputs
  selectedOptionId?: string;
  selectedOptionLabel?: string;
  savedAnswer?: 'yes' | 'no' | string;
  savedDate?: string;

  /**
   * Read-time conditional-visibility verdict for this instance,
   * computed by the instances endpoint from the definition's showWhen
   * condition and the sibling instances' stored answers. NEVER
   * persisted — visibility is always re-derived on read, so reopening
   * or re-answering the source task re-derives it with no migration.
   */
  showWhenState?: { visible: boolean; broken: boolean };

  /**
   * Backend send runs (2026-10-27 retrigger + async-sends feature).
   * Every send-bearing completion and every admin retrigger dispatches
   * its email / automation through a backend job; one entry per
   * dispatch is appended to `sendRuns` and `latestSendRun` mirrors the
   * most recent, so the run's lifecycle is recorded on the instance.
   * The `status` on a stored entry is a cache refreshed from the run
   * record whenever instances are read — the run record (bulk job
   * item / automation run) is the source of truth.
   */
  sendRuns?: PhaseActionSendRun[];
  latestSendRun?: PhaseActionSendRun;
  /**
   * Read-time lifecycle annotation computed server-side from the run
   * record — or, for instances completed before this feature, from the
   * stored emailMessageId / automationRunId (`legacy: true`). NEVER
   * persisted. The drawer renders the status chip and the Resend /
   * Re-run control from this alone.
   */
  sendRunState?: PhaseActionSendRunState;

  // Completion Metadata
  completedAt?: string;
  completedByUid?: string;
  completedByName?: string;

  // History Audit Trail
  history?: Array<{
    action: 'completed' | 'reopened' | 'executed';
    timestamp: string;
    byUid: string;
    byName: string;
    reason?: string;
    details?: any;
  }>;
}

/** Lifecycle of one backend-dispatched send (email or automation run). */
export type PhaseActionSendRunStatus = 'queued' | 'running' | 'sent' | 'failed';

/**
 * One dispatched send run linked to a phase-action instance. Stored on
 * the instance (append-only in `sendRuns`, mirrored in `latestSendRun`).
 * `runId` is the bulk-job id for emails and the automation-run id for
 * automations; `itemId` is the bulk-job item doc id (emails only).
 */
export interface PhaseActionSendRun {
  runId: string;
  itemId?: string;
  kind: 'email' | 'automation';
  /** True when this run was dispatched by an admin retrigger. */
  retrigger: boolean;
  queuedAt: string;
  byUid: string;
  byName: string;
  /** What is being sent — the email template name / subject, or the automation name. */
  label: string;
  /** Recipient email for email runs. */
  recipient?: string;
  automationName?: string;
  /** Cached lifecycle verdict; refreshed from the run record on read. */
  status: PhaseActionSendRunStatus;
  /** Plain-language failure reason once `status` is 'failed'. */
  error?: string;
  /** ISO time the cached status last converged from the run record. */
  resolvedAt?: string;
}

/**
 * Read-time view of an instance's latest send run, computed by the
 * server from the run record. `retriggerable` is true only when the
 * latest run is terminal (sent / failed) — never while one is queued
 * or running. `legacy` marks instances completed under the old
 * synchronous flow (stored emailMessageId / automationRunId, no run
 * record): they read as 'sent'.
 */
export interface PhaseActionSendRunState {
  kind: 'email' | 'automation';
  status: PhaseActionSendRunStatus;
  retrigger: boolean;
  label: string;
  recipient?: string;
  queuedAt: string;
  error?: string;
  retriggerable: boolean;
  legacy: boolean;
}
